import { addLog, updateStatus, progressBar } from "./logger";
// import monitorMemory from './monitorMemory';
import dcmjs from "dcmjs";
import DicomDict2 from "./types/DicomDict2";

import { cornerstone } from "./cornerstone/cornerstone-setup.js";

import { Ref } from "vue";
import aTick from "./aTick";
import shouldQuarantine from "./shouldQuarantine";
import CaseDef from "./types/CaseDef";
import Instance, { FrameData } from "./types/Instance";
import Series from "./types/Series";
import { collapseSeriesParts, splitSeries } from "./display-sets";
import { recordInstanceLoaded } from "./session-stats";
import * as fileRegistry from "./cornerstone/file-registry";
import * as instanceSource from "./cornerstone/instance-source";
// import convertDataSet from "./dataSetToDict";

/** Read a File fully into an ArrayBuffer (the raw DICOM P10 bytes). */
function readArrayBuffer(file: File): Promise<ArrayBuffer> {
	return new Promise((resolve, reject) => {
		const fileReader = new FileReader();
		fileReader.onload = () => resolve(fileReader.result as ArrayBuffer);
		fileReader.onerror = () => reject(fileReader.error);
		fileReader.readAsArrayBuffer(file);
	});
}

/** NumberOfFrames (0028,0008), defaulting to 1. */
function getNumberOfFrames(dataset: DicomDict2): number {
	const raw = dataset.dict["00280008"]?.Value?.[0];
	if (raw == null) return 1;
	const n = parseInt(String(raw), 10);
	return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Integer tag value, or `fallback` when absent/unparseable. */
function getIntTag(dataset: DicomDict2, tag: string, fallback: number): number {
	const raw = dataset.dict[tag]?.Value?.[0];
	if (raw == null) return fallback;
	const n = parseInt(String(raw), 10);
	return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Size of one frame's pixel payload, computed from the header rather than by
 * decoding: Rows × Columns × SamplesPerPixel × ceil(BitsAllocated / 8).
 *
 * This is the DICOM payload size, which is what the exported file contains. Note
 * it's not quite the in-memory footprint for color: the decoder expands RGB to
 * RGBA, so a color frame occupies 4/3 of this while it's cached. Actual memory is
 * reported separately as the image cache peak.
 */
function estimateFrameBytes(dataset: DicomDict2): number {
	const rows = getIntTag(dataset, "00280010", 0);
	const columns = getIntTag(dataset, "00280011", 0);
	if (!rows || !columns) return 0;
	const samplesPerPixel = getIntTag(dataset, "00280002", 1);
	const bytesPerSample = Math.ceil(getIntTag(dataset, "00280100", 8) / 8);
	return rows * columns * samplesPerPixel * bytesPerSample;
}

/**
 * One instance's bytes, however they reached us.
 *
 * `url` is fetched by cornerstone and has no re-readable source of its own.
 * `file` is a user-picked file. `bytes` is an in-memory buffer with no file
 * behind it (a DICOMweb retrieval) — the registry spools those to OPFS so this
 * loader can drop the buffer as soon as it's parsed.
 */
export type LoadSource =
	| { kind: "url"; url: string }
	| { kind: "file"; file: File }
	| { kind: "bytes"; bytes: ArrayBuffer; name: string };

export interface LoadResult {
	loaded: number;
	failed: number;
}

/**
 * Single-pass, per-instance loader.
 *
 * For each file we:
 *   1. Read the raw P10 buffer once.
 *   2. Parse the header with dcmjs (untilTag pixel data) -> editable dataset.
 *   3. Run the quarantine check BEFORE decoding pixels, so files we'll reject
 *      never pay for a decode.
 *   4. Decode frame 0 only, to prove the file's pixel data is readable.
 *   5. Drop the raw buffer + parsed dataSet for this instance before moving on.
 *
 * Loading is O(files), not O(frames). Frames beyond the first are never touched
 * here: the viewer decodes only what it displays (there is no prefetcher), and the
 * export writer decodes each frame as it streams it. Decoding all of them up front
 * was pure work for any frame the user never scrolled to, and on a large study it
 * meant materializing the entire decompressed dataset — many times the size of the
 * files on disk — before the user could do anything.
 *
 * The frame list and the decoded-size stats come from the header instead
 * (NumberOfFrames, Rows/Columns/SamplesPerPixel/BitsAllocated). The tradeoff: a
 * corrupt frame deep inside a file now surfaces when it's viewed or exported
 * rather than during load. Frame 0 still catches a file that won't decode at all.
 */
export default async function loadImages(
	imageSources: {
		urls?: string[];
		files?: File[];
		buffers?: { bytes: ArrayBuffer; name: string }[];
	},
	caseDef: Ref<CaseDef>,
	silent: boolean,
	storageProvider?: string
): Promise<LoadResult> {
	const { urls, files, buffers } = imageSources;
	const studies = caseDef.value.studies;

	// Normalized up front — and note this happens synchronously, before the first
	// await, so a caller that reuses its input array after the call still gets
	// every source it handed over.
	const sources: (LoadSource | null)[] = [];
	if (urls) for (const url of urls) sources.push({ kind: "url", url });
	if (files) for (const file of files) sources.push({ kind: "file", file });
	if (buffers)
		for (const b of buffers)
			sources.push({ kind: "bytes", bytes: b.bytes, name: b.name });

	let failedLoad = 0;
	let failedParse = 0;
	let loaded = 0;

	if (!silent) progressBar.value.start(sources.length);

	for (let sourceIndex = 0; sourceIndex < sources.length; sourceIndex++) {
		const source = sources[sourceIndex]!;
		// Release the list's reference as we go. For `bytes` sources this is the
		// difference between the queue holding one instance and holding the whole
		// batch: once registered and parsed, nothing should still point at the
		// buffer.
		sources[sourceIndex] = null;

		await aTick();

		// Progress accounting: each instance contributes exactly 1.0 to numDone.
		// We advance fractionally within a multiframe instance (numDone =
		// instanceBase + framesDone/numberOfFrames) so the bar moves while a single
		// large multiframe decodes, then snap to instanceBase + 1 in `finally`
		// (covering every early-continue / error path). numDone is an integer at
		// the top of each iteration because of that snap.
		const instanceBase = progressBar.value.numDone;

		// A `url` source is fetched and decoded by cornerstone and has no re-readable
		// local copy; everything else is registered so any frame can be re-decoded
		// from its source later.
		const isRemoteUrl = source.kind === "url";
		const originalFileName =
			source.kind === "file"
				? source.file.name
				: source.kind === "bytes"
					? source.name
					: undefined;

		// registerBytes spools to OPFS and never throws — a storage failure falls
		// back to holding the bytes in memory rather than failing the import.
		const imageId =
			source.kind === "url"
				? `wadouri:${source.url}`
				: source.kind === "file"
					? fileRegistry.registerFile(source.file)
					: await fileRegistry.registerBytes(source.bytes, source.name);

		// `buffer` is the raw P10; `urlSeedImage` is the frame-0 image cornerstone
		// decodes while fetching a URL (reused so we don't decode it twice).
		let buffer: ArrayBuffer | null = null;
		let urlSeedImage: any = null;
		let acquiredUrl: string | null = null;
		// Cleared once the instance is in the case. Anything else — a failed parse,
		// a hard quarantine, a thrown error — has to give its registry entry back.
		let instanceAdded = false;

		try {
			if (!silent) updateStatus(`Loading image: ${imageId}`);

			// --- 1. Obtain raw buffer + 2. parse header --------------------------
			let dataset: DicomDict2;
			try {
				if (source.kind === "file") {
					// Read once; the same buffer feeds both dcmjs and the decoder below.
					buffer = await readArrayBuffer(source.file);
				} else if (source.kind === "bytes") {
					// Already in hand. It's spooled to OPFS now, so this reference is the
					// only thing keeping it on the heap and it's dropped below.
					buffer = source.bytes;
					(source as { bytes: ArrayBuffer | null }).bytes = null;
				} else {
					// Let cornerstone fetch the URL (preserves DICOMweb auth/config),
					// then reuse the bytes it parsed for our header parse.
					urlSeedImage = await cornerstone.loadAndCacheImage(imageId);
					buffer = urlSeedImage.data.byteArray.buffer;
				}

				dataset = dcmjs.data.DicomMessage.readFile(buffer as ArrayBuffer, {
					// Parse only the header — pixel data comes from the cornerstone
					// decode. (Also avoids a dcmjs overrun seen on malformed private
					// tags that sit after the image data in some files.)
					untilTag: "7FE00010",
					includeUntilTagValue: false,
				});
				// dataset = convertDataSet(urlSeedImage?.data) as DicomDict2; // parse-once alt
			} catch (err) {
				const msg =
					(err as any)?.message || (err as any)?.error?.message || String(err);
				addLog(
					"error",
					`Error reading/parsing file "${originalFileName || imageId}": ${msg}`
				);
				console.error(err);
				failedParse++;
				continue;
			}

			const compressedBytes = (buffer as ArrayBuffer).byteLength;
			const numberOfFrames = getNumberOfFrames(dataset);

			// --- 3. Quarantine BEFORE decoding pixels ----------------------------
			const quarantine = shouldQuarantine(dataset, storageProvider);
			if (quarantine && quarantine.action === "fail") {
				addLog(
					"error",
					`Failed to load image "${originalFileName || imageId}": ${quarantine.reason}`
				);
				failedParse++;
				continue; // never decode frames for a hard-rejected file
			}

			// --- 4. Build the frame list; decode only frame 0 --------------------
			const frames: FrameData[] = [];
			for (let frameIndex = 0; frameIndex < numberOfFrames; frameIndex++) {
				frames.push({
					// Single-frame instances keep the bare imageId (no ?frame= suffix),
					// matching what the loaders and the viewer expect.
					imageId:
						numberOfFrames > 1 ? `${imageId}?frame=${frameIndex}` : imageId,
					originalFrameIndex: frameIndex,
				});
			}

			if (!silent && numberOfFrames > 1)
				updateStatus(
					`Loading multiframe image: ${imageId} (${numberOfFrames} frames)`
				);

			if (!isRemoteUrl) {
				// Pin the parsed file using the bytes we already read, so dicom-parser
				// doesn't go back to disk (or OPFS) for them.
				acquiredUrl = instanceSource.urlFor(imageId);
				await instanceSource.acquire(
					acquiredUrl,
					imageId,
					buffer as ArrayBuffer
				);
				// Validation decode. loadImage (not loadAndCacheImage) — nothing needs
				// this frame yet, and caching it here would just push out frames the
				// viewer is about to ask for.
				await cornerstone.loadImage(frames[0].imageId);
			} else if (urlSeedImage) {
				// URL path: cornerstone already decoded frame 0 while fetching. Release
				// its dataSet reference so the cached image doesn't pin the whole file.
				urlSeedImage.data = null;
			}

			if (numberOfFrames > 1)
				addLog(
					"info",
					`${imageId}${
						originalFileName ? ` (${originalFileName})` : ""
					}: ${numberOfFrames} frames`
				);

			// --- 5. Free this instance's raw buffer + parsed dataSet -------------
			buffer = null;
			urlSeedImage = null;
			if (acquiredUrl) {
				instanceSource.release(acquiredUrl);
				acquiredUrl = null;
			}

			recordInstanceLoaded({
				compressedBytes,
				// Derived from the header rather than measured, since we no longer
				// decode every frame just to add up their sizes.
				decompressedBytes: estimateFrameBytes(dataset) * numberOfFrames,
				frameCount: numberOfFrames,
			});

			// --- Sort into Study -> Series -> Instance ---------------------------
			const studyInstanceUID = dataset.dict["0020000D"]?.Value[0];
			let study = studies.find((s) => s.studyInstanceUID === studyInstanceUID);
			if (!study) {
				study = {
					uid: crypto.randomUUID(),
					studyInstanceUID,
					studyDescription: dataset.dict["00081030"]?.Value[0],
					studyDate: dataset.dict["00080020"]?.Value[0],
					series: [],
				};
				studies.push(study);
			}
			const seriesInstanceUID = dataset.dict["0020000E"]?.Value[0];
			let series = study.series.find(
				(s) => s.seriesInstanceUID === seriesInstanceUID
			);
			if (!series) {
				series = {
					uid: crypto.randomUUID(),
					seriesInstanceUID,
					seriesDescription: dataset.dict["0008103E"]?.Value[0],
					modality: dataset.dict["00080060"]?.Value[0],
					instances: [],
				};
				study.series.push(series);
			}

			const instance: Instance = {
				uid: crypto.randomUUID(),
				imageId,
				sopInstanceUID: dataset.dict["00080018"]?.Value[0],
				numberOfFrames,
				frames,
				instanceData: {
					originalFileName,
					dicomDataset: dataset,
					dicomP10ArrayBuffer: null,
					dicomP10AnonymizedArrayBuffer: null,
				},
				quarantine,
			};
			series.instances.push(instance);
			instanceAdded = true;
			loaded++;

			if (
				quarantine &&
				quarantine.action === "quarantine" &&
				!caseDef.value.markedForRemoval.seriesUIDs.includes(series.uid)
			) {
				caseDef.value.markedForRemoval.seriesUIDs.push(series.uid);
			}

			if (!silent) updateStatus(`Loading image: ${imageId}... done`);
			addLog(
				"info",
				`${imageId}${
					originalFileName ? ` (${originalFileName})` : ""
				}: Loaded & parsed DICOM`
			);
		} catch (err) {
			let errMessage =
				(err as any)?.error?.message || (err as any)?.message || err;
			if (typeof errMessage !== "string") errMessage = JSON.stringify(errMessage);
			addLog(
				"error",
				`Error loading file${
					originalFileName ? ` "${originalFileName}"` : ` ${imageId}`
				}: ${errMessage}`
			);
			console.error(err);
			failedLoad++;
		} finally {
			buffer = null;
			urlSeedImage = null;
			// Covers the error paths — on success this already ran above.
			if (acquiredUrl) instanceSource.release(acquiredUrl);
			// Order matters: drop the parse pin before the bytes it was parsed from.
			if (!instanceAdded && !isRemoteUrl) {
				fileRegistry.release(instanceSource.urlFor(imageId));
			}
			// Snap to the next whole unit regardless of path (success, parse-error
			// continue, or thrown error), correcting any fractional frame progress.
			if (!silent) progressBar.value.numDone = instanceBase + 1;
		}
	}

	// Drop everything except the one file left hot by the last iteration.
	//
	// Deliberately not releaseAll(): the preview viewer mounts immediately after
	// this and acquires a file straight away, so discarding the hot parse here
	// means reading and re-parsing it off disk moments later. For a single large
	// multiframe instance — where that one file is the entire study — that's a
	// redundant full-size read, and briefly two copies resident while the first
	// awaits garbage collection. Holding one parsed file costs no more than the
	// load loop already did, and the viewer's first release evicts it if it turns
	// out to be the wrong one.
	instanceSource.releaseIdle();

	if (!silent) progressBar.value.reset();

	// Regroup before sorting. A series may already have been split by an earlier
	// call (the DICOMweb importer retrieves in batches and calls back in for each
	// one), and this batch's instances landed on whichever part happened to match
	// their SeriesInstanceUID. Collapsing first means the sort and the split below
	// always see the whole source series, so the outcome doesn't depend on how the
	// instances were batched.
	const partUidsBefore = new Set<string>();
	for (const study of studies) {
		for (const series of study.series) partUidsBefore.add(series.uid);
		study.series = collapseSeriesParts(study.series);
	}

	// Sort series instances by instanceNumber
	if (!silent) updateStatus(`Sorting images...`);
	for (const study of studies) {
		for (const series of study.series) {
			series.instances.sort((a, b) => {
				if (!a.instanceData.dicomDataset || !b.instanceData.dicomDataset) return 0;
				const aNum = parseInt(
					a.instanceData.dicomDataset.dict["00200013"]?.Value?.[0] as string,
					10
				);
				const bNum = parseInt(
					b.instanceData.dicomDataset.dict["00200013"]?.Value?.[0] as string,
					10
				);
				if (isNaN(aNum) || isNaN(bNum)) return 0;
				return aNum - bNum;
			});
		}
	}

	// Split heterogeneous series into independent editing units. Runs after the
	// sort so parts come out in InstanceNumber order. Deterministic: the same
	// instances always produce the same parts with the same uids, however many
	// batches they arrived in. See display-sets.ts for what gets split and why.
	const removedSeriesUids = caseDef.value.markedForRemoval.seriesUIDs;
	for (const study of studies) {
		const splitSeriesList: Series[] = [];
		for (const series of study.series) {
			const parts = splitSeries(series);
			if (parts.length > 1) {
				// Quarantine marks a series for removal by uid during the load loop above.
				// Part 1 keeps the source uid; the rest need the mark carried across, or a
				// flagged series would come back half-included.
				if (removedSeriesUids.includes(series.uid)) {
					for (const part of parts.slice(1)) {
						if (!removedSeriesUids.includes(part.uid)) {
							removedSeriesUids.push(part.uid);
						}
					}
				}
				// Only announce a layout the user hasn't been told about: re-splitting an
				// unchanged series on every batch would bury the log. The label list is
				// capped because a series whose stills vary in size image-to-image (some
				// spectral captures do, by a row or two) splits into dozens of parts —
				// correct, since the rectangles genuinely don't transfer between them, but
				// not something to print in full.
				if (parts.some((part) => !partUidsBefore.has(part.uid))) {
					const labels = parts.map((part) => part.displaySet!.label);
					const shown = labels.slice(0, 8).join(", ");
					addLog(
						"info",
						`${
							series.seriesDescription || series.seriesInstanceUID
						}: split into ${parts.length} parts (${shown}${
							labels.length > 8 ? `, +${labels.length - 8} more` : ""
						})`
					);
				}
			}
			splitSeriesList.push(...parts);
		}
		study.series = splitSeriesList;
	}

	const failed = failedLoad + failedParse;

	if (!silent) {
		const total = sources.length;
		if (loaded === 0) {
			updateStatus(`No valid DICOM files were loaded.`);
		} else if (failed === 0) {
			updateStatus(`Loaded ${loaded} file${loaded === 1 ? "" : "s"} successfully.`);
		} else {
			updateStatus(
				`Loaded ${loaded} of ${total} file${total === 1 ? "" : "s"}. ${failed} failed — expand logs for details.`
			);
		}
	}

	// Returned so a caller that drives this in silent batches (the DICOMweb
	// importer) can total the counts and report once at the end.
	return { loaded, failed };
}
